/**
 * EmailChangeConfirmPage - Step 3 of email change flow (#1335)
 *
 * User lands here after clicking the verification link sent to their NEW email.
 * Automatically completes the email change on mount.
 *
 * Route: /settings/email-change/confirm?token=xxx
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../../lib/api/auth';
import { ApiError } from '../../lib/api';
import { TopBar, Button, OroLoader } from '../../components';
import { AlertCircle, CheckCircle } from 'lucide-react';

export default function EmailChangeConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  // React docs "adjust state on prop change" — surface "missing token" error
  // immediately during render rather than via effect.
  const [prevToken, setPrevToken] = useState(token);
  if (prevToken !== token) {
    setPrevToken(token);
    if (!token) {
      setStatus('error');
      setError('Invalid or missing token.');
    }
  }

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    const completeChange = async () => {
      try {
        await authApi.completeEmailChange(token);
        if (!cancelled) {
          setStatus('success');
          // Refresh user data so the new email shows up
          queryClient.invalidateQueries({ queryKey: ['me'] });
          queryClient.invalidateQueries({ queryKey: ['profile'] });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof ApiError
            ? err.message
            : 'Failed to complete email change.';
          setError(message);
          setStatus('error');
        }
      }
    };

    completeChange();
    return () => { cancelled = true; };
  }, [token, queryClient]);

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Email Change" onBack={() => navigate('/settings/profile')} />
      <div className="flex-1 flex items-center justify-center px-4 pt-14">
        {status === 'loading' && (
          <div className="text-center space-y-4">
            <OroLoader className="mx-auto" decorative />
            <p className="text-text-secondary text-sm">Confirming your new email...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center space-y-4 max-w-sm">
            <CheckCircle className="w-12 h-12 text-oro-success mx-auto" />
            <p className="text-text-primary font-medium">Email updated</p>
            <p className="text-text-secondary text-sm">
              Your email address has been changed successfully. For security, all sessions have been signed out. Please sign in with your new email.
            </p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Sign In
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center space-y-4 max-w-sm">
            <AlertCircle className="w-12 h-12 text-oro-error mx-auto" />
            <p className="text-text-primary font-medium">Something went wrong</p>
            <p className="text-text-secondary text-sm">{error}</p>
            <Button variant="secondary" onClick={() => navigate('/settings/profile')}>
              Back to Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
